import { LogOut, authActionTypes } from './../../auth/store/auth.actions';
import { AppState } from './../../store/reducers/index';
import { Store } from '@ngrx/store';
import { EventEmitter, Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { BroadcastService } from '../../services/broadcast.service';
import { ErrorHandlerService } from '../../services/error-handler.service';
import { UserRegistration } from '../models/user-registration/user-registration.model';
import { User } from '../models/user.model';
import { LoginAndUserApiService } from '../services/login-and-user-api.service';
import { LocalCacheManager } from './local-cache.manager';
import { NotificationManagerService } from './notification-manager.service';
import { AuthState } from './../../auth/store/auth.entity';
import { Constants } from './../../core/constants';
import { loggedInUser } from './../../auth/store/auth.selectors';
import { NotificationPreference } from '../models/notification-preference.model';

@Injectable()
export class LoginAndUserManagerService {
    // Events
    userChanged = new EventEmitter<User>();
    Constants = Constants;
    // Member variables
    private _currentUser: User;
    public redirectUrl = '/dashboard';

    constructor(private loginAndUserApiService: LoginAndUserApiService,
        private localCacheManager: LocalCacheManager,
        private broadcastService: BroadcastService,
        private notificationManager: NotificationManagerService,
        private store: Store<AppState>,
        private authStore: Store<AuthState>,
        private ngZone: NgZone,
        private router: Router) {
        this.store.select(loggedInUser).subscribe(user => {
            console.log(user);
            if (this._currentUser === undefined || this._currentUser === null) {
                this._currentUser = user;
            }
        });
    }

    // Public Properties
    get isUserLoggedIn(): boolean {

        return this._currentUser && this._currentUser.Token.length !== 0;
    }

    get currentUser(): User {
        return this._currentUser;// || this.localCacheManager.currentUser;
    }

    set currentUser(user: User) {
        this._currentUser = user;
        this.localCacheManager.currentUser = this._currentUser;

        if (this._currentUser) {
            this.userChanged.emit(this._currentUser);
        }
    }

    checkLocalStorageVersion() {
        let lsVersion = localStorage.getItem('appVersion');
        if (this.Constants.AppVersion !== lsVersion) {
            this.signOut();
            // this.store.dispatch(authActionTypes.LogOut());
        }
    }

    // Public Methods
    login(email: string, password: string): Observable<User> {
        return this.loginAndUserApiService.loginUser(email, password)
            .pipe(
                tap(user => {
                    this.currentUser = user;
                    this.localCacheManager.accessToken = user.Token;
                    this.localCacheManager.userId = user.Id;
                }, err => {
                    console.log(err);
                })
            );
    }

    signOut() {

        // this.authStore.dispatch(authActionTypes.LogOutSuccess());

        this.localCacheManager.accessToken = '';
        this.localCacheManager.currentUser = null;
        // this.localCacheManager.emailAddress=null;

        // this.localCacheManager.currentUser = null;
        // this.localCacheManager.
        this.redirectUrl = '/dashboard';

        // Let others know
        this.broadcastService.userSignedOut.next(this.currentUser);
        this.notificationManager.stopPolling();

        this.userChanged.emit(this.currentUser);
        this.currentUser = null;
        localStorage.removeItem('locationState');
        localStorage.removeItem('accountState');
        localStorage.removeItem('authState');
        localStorage.removeItem('lightingState');
        sessionStorage.removeItem('WhatsNewClosed');
        this.store.dispatch(authActionTypes.LogOut());
        this.ngZone.run(() => {
            this.router.navigate(['/welcome']);
        });
    }

    getUserDetails(): Observable<User | {}> {
        return this.loginAndUserApiService.getUserDetails()
            .pipe(
                map(user => {
                    return this.currentUser = user;
                })
            );
    }

    registerUser(userRegistration: UserRegistration): Observable<User | {}> {
        return this.loginAndUserApiService.registerUser(userRegistration)
            .pipe(
                catchError(error => ErrorHandlerService.handleError(this, error, 'registerUser()', true))
            );
    }

    sendConfirmationEmail(email: string) {
        return this.loginAndUserApiService.sendConfirmationEmail(email)
            .pipe(
                catchError(error => ErrorHandlerService.handleError(this, error, 'sendConfirmationEmail()', true))
            );
    }

    forgotPassword(email: string) {
        return this.loginAndUserApiService.forgotPassword(email);
    }

    resetPassword(password: string, id: string, secret: string) {
        return this.loginAndUserApiService.resetPassword(password, id, secret);
    }

    validateUser(id: string, secret: string) {
        return this.loginAndUserApiService.validateUser(id, secret)
            .pipe(
                catchError(error => ErrorHandlerService.handleError(this, error, 'validateUser()', true))
            );
    }

    validateResetLink(id: string, secret: string) {
        return this.loginAndUserApiService.validateResetLink(id, secret)
            .pipe(
                catchError(error => ErrorHandlerService.handleError(this, error, 'validateResetLink()', true))
            );
    }

    updateUserInfo(user: User): Observable<any> {
        return this.loginAndUserApiService.updateUserInfo(user);
    }

    updateUserPassword({ oldPassword, newPassword, newPasswordConfirm }): Observable<any> {
        return this.loginAndUserApiService.updateUserPassword({ oldPassword: oldPassword, newPassword: newPassword, newPasswordConfirm: newPasswordConfirm });
    }

    updateUserEmail({ oldEmail, newEmail, password }): Observable<any> {
        return this.loginAndUserApiService.updateUserEmail({ oldEmail: oldEmail, newEmail: newEmail, password: password });
    }

    resetEmail({ id, email, secret }): Observable<any> {
        return this.loginAndUserApiService.resetEmail({ id: id, email: email, secret: secret });
    }

    // Helper Methods

    getNotificationPreferences(userId: number): Observable<any> {
        return this.loginAndUserApiService.getNotificationPreferences(userId);
    }

    setNotificationPreferences(id: number, preferences: NotificationPreference[]): Observable<any> {
        return this.loginAndUserApiService.saveNotificationPreferences(id, preferences);
    }
}
